import { z } from "zod";
import { useTRPC } from "../../trpc";
import { useCallback, useMemo } from "react";
import { RelativeLink } from "../../components/Link";
import { Alert, Button, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { FormattedMessage, useIntl } from "react-intl";
import { emailSchema } from "../../utils/schema";
import { captureEvent } from "../../utils/posthog";
import { useMutation } from "@tanstack/react-query";
import { Event } from "../../constants/events";
import { IconAt, IconExclamationCircle } from "@tabler/icons-react";

const initialValues = {
  email: "",
};

export function RequestPasswordReset() {
  const intl = useIntl();
  const trpc = useTRPC();

  const { mutate, isPending, isSuccess, error } = useMutation(trpc.requestResetPassword.mutationOptions());

  const validate = useMemo(() => zod4Resolver(
    z.object({
      email: emailSchema(intl),
    }),
  ), [intl]);

  const { onSubmit, getInputProps } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  const handleSubmit = useCallback((form: typeof initialValues) => {
    captureEvent(Event.AuthRequestResetPassword, {
      username: form.email,
    });
    mutate({
      username: form.email,
    });
  }, [mutate]);

  return (
    <Stack>
      <form onSubmit={onSubmit(handleSubmit)}>
        <Stack gap="md">
          {error && (
            <Alert variant="light" color="red" icon={<IconExclamationCircle />}>
              <FormattedMessage
                id="error.invalidEmailCheck"
                defaultMessage="Check your email address and try again"
              />
            </Alert>
          )}

          {!isSuccess && (
            <>
              <Title size="h2">
                <FormattedMessage id="title.forgotPassword" defaultMessage="Forgot your password?" />
              </Title>
              <Text>
                <FormattedMessage
                  id="text.enterEmail"
                  defaultMessage="Please enter the email address associated with your account. We will email you a link to reset your password."
                />
              </Text>
              <TextInput
                label={<FormattedMessage id="label.email" defaultMessage="Email" />}
                placeholder={intl.formatMessage({
                  id: "placeholder.email",
                  defaultMessage: "Your email",
                })}
                leftSection={<IconAt size={16} />}
                required
                {...getInputProps("email")}
              />
              <Stack gap="1rem" mt="xs">
                <Button type="submit" fullWidth loading={isPending} variant="primary">
                  <FormattedMessage id="button.sendRequest" defaultMessage="Send request" />
                </Button>

                <RelativeLink underline="always" href="../sign-in">
                  <FormattedMessage id="link.returnToSignIn" defaultMessage="Return to sign in" />
                </RelativeLink>
              </Stack>
            </>
          )}

          {isSuccess && (
            <>
              <Title size="h3">
                <FormattedMessage id="title.instructionsSent" defaultMessage="Instructions sent!" />
              </Title>
              <Text>
                <FormattedMessage
                  id="text.instructionsSentMessage"
                  defaultMessage="We've sent password reset instruction on how to reset your password to email address your provided. If you do not get an email please check your spam folder."
                />
              </Text>

              <RelativeLink underline="always" href="../sign-in" onClick={() => { captureEvent(Event.AuthRegisteredAccount); }}>
                <FormattedMessage id="link.returnToSignIn" defaultMessage="Return to sign in" />
              </RelativeLink>
            </>
          )}
        </Stack>
      </form>
    </Stack>
  );
}
